import type { Choice, Quiz } from '../types'

const ok = (label: string): Choice => ({ label, correct: true })
const no = (label: string): Choice => ({ label })

const pick = (prompt: string, choices: Choice[], explanation: string): Quiz => ({
  format: 'quick_pick',
  multi: true,
  prompt,
  choices,
  explanation,
})

const spot = (prompt: string, code: string, choices: Choice[], explanation: string): Quiz => ({
  format: 'spot',
  multi: true,
  prompt,
  code,
  choices,
  explanation,
})

const spot1 = (prompt: string, code: string, choices: Choice[], explanation: string): Quiz => ({
  format: 'spot',
  prompt,
  code,
  choices,
  explanation,
})

const judge = (prompt: string, truth: boolean, explanation: string, code?: string): Quiz => ({
  format: 'judge',
  prompt,
  ...(code ? { code } : {}),
  choices: [truth ? ok('True') : no('True'), truth ? no('False') : ok('False')],
  explanation,
})

export const MORE_COMBOS: Record<string, Quiz[]> = {
  'strategy-factory-method': [
    pick(
      'Pricing rules are swappable classes. A method returns the right one for the customer tier. Which patterns?',
      [no('Adapter'), ok('Strategy'), ok('Factory Method'), no('Facade'), no('Memento')],
      'Swappable rules are strategies; the method that returns the right one is a factory method.',
    ),
    pick(
      'Which patterns together let callers sort with any algorithm without naming a concrete class?',
      [ok('Strategy'), no('Observer'), no('Visitor'), ok('Factory Method')],
      'Strategy gives the interchangeable algorithm; a factory method hides which class is built.',
    ),
    spot(
      'Which patterns are here?',
      `type Shipper interface{ Cost(kg int) int }

func newShipper(kind string) Shipper {
	switch kind {
	case "air":
		return Air{}
	default:
		return Road{}
	}
}`,
      [ok('Strategy'), ok('Factory Method'), no('Decorator'), no('Singleton'), no('Composite')],
      'Shipper is the strategy interface; newShipper picks and builds one for the caller.',
    ),
    spot1(
      'Which pattern is here, or is it misused?',
      `class Mailer {
    function send($to, $kind) {
        if ($kind === 'smtp') {
            $t = new SmtpTransport();
        } else {
            $t = new LogTransport();
        }
        $t->deliver($to);
    }
}`,
      [no('Strategy'), no('Factory Method'), no('None of these'), ok('Misused')],
      'Transports are strategies, but the if/else is inlined where it is used: a misuse.',
    ),
    judge(
      'A Strategy always needs a Factory Method to work.',
      false,
      'You can pass a strategy in directly; a factory only helps when something has to choose it.',
    ),
  ],
  'decorator-proxy': [
    pick(
      'A wrapper adds retry logic, another delays creating a heavy object until first use. Both keep the interface. Which?',
      [ok('Decorator'), no('Bridge'), ok('Proxy'), no('Flyweight')],
      'Retry adds behaviour (decorator); delayed creation controls access (virtual proxy).',
    ),
    pick(
      'Which patterns can wrap an object while keeping its interface?',
      [no('Singleton'), ok('Decorator'), no('Builder'), ok('Proxy'), no('Prototype')],
      'Both hold an object of the same interface; Singleton, Builder and Prototype do not wrap.',
    ),
    spot(
      'Which patterns are here?',
      `type Store interface{ Get(k string) string }

type Timed struct{ in Store }

func (t Timed) Get(k string) string {
	defer track(time.Now())
	return t.in.Get(k)
}

s := Timed{Cached{NewDB()}}`,
      [no('Adapter'), ok('Decorator'), ok('Proxy'), no('Facade'), no('Strategy')],
      'Timed layers timing on top; Cached stands in for the DB and answers from memory.',
    ),
    spot1(
      'Which pattern is here, or is it misused?',
      `class Secure implements Files {
    function __construct(private $f) {}
    function read(string $p): string {
        if (! Gate::allows('read', $p)) {
            abort(403);
        }
        return $this->f->read($p);
    }
}`,
      [ok('Proxy'), no('Decorator'), no('None of these'), no('Misused')],
      'It guards access to the real object, so it is a protection proxy, not a decorator.',
    ),
    judge(
      'A decorator and a proxy never share the same class shape.',
      false,
      'Both wrap an object with the same interface; only the intent differs.',
    ),
  ],
  'composite-visitor': [
    pick(
      'A file tree is folders holding files. A SizeReport class is handed to each node to total sizes. Which patterns?',
      [no('Flyweight'), ok('Composite'), no('Prototype'), ok('Visitor')],
      'Nested folders are a composite; SizeReport is a visitor added without editing nodes.',
    ),
    pick(
      'You want a new operation over a tree without editing each node class. Which patterns fit?',
      [ok('Visitor'), no('Decorator'), ok('Composite'), no('Mediator'), no('State')],
      'The tree is a composite; each new operation is a new visitor class.',
    ),
    spot(
      'Which patterns are here?',
      `type Node interface{ Accept(v Visitor) }

type Group struct{ Kids []Node }

func (g Group) Accept(v Visitor) {
	for _, k := range g.Kids {
		k.Accept(v)
	}
	v.Group(g)
}`,
      [ok('Composite'), ok('Visitor'), no('Iterator'), no('Proxy'), no('Memento')],
      'Group holds child nodes and passes the visitor down, then calls it on itself.',
    ),
    spot(
      'Which patterns are here?',
      `$tree = new Div([new Text('a'), new Div()]);

class Upper implements Visitor {
    function text(Text $t) {
        $t->value = strtoupper($t->value);
    }
}

$tree->accept(new Upper());`,
      [ok('Visitor'), ok('Composite'), no('Strategy'), no('Singleton'), no('Builder')],
      'Div nests nodes (composite); Upper is a visitor run across them with accept().',
    ),
    judge(
      'A visitor makes it cheap to add new node types to a composite.',
      false,
      'It makes new operations cheap; each new node type forces a change in every visitor.',
    ),
  ],
  'composite-iterator': [
    pick(
      'An org chart has managers who hold teams of staff. A loop lists everyone without caring about depth. Which patterns?',
      [ok('Iterator'), no('Command'), no('Proxy'), ok('Composite')],
      'The nesting is a composite; the loop that hides the recursion is an iterator.',
    ),
    pick(
      'You want depth-first and breadth-first walks over one tree without changing its nodes. Which patterns?',
      [no('Visitor'), ok('Iterator'), ok('Composite'), no('Adapter'), no('Bridge')],
      'The tree stays a composite; each walk order is a separate iterator.',
    ),
    spot(
      'Which patterns are here?',
      `func (n *Node) Walk(yield func(*Node)) {
	yield(n)
	for _, c := range n.Kids {
		c.Walk(yield)
	}
}

for n := range tree.All() {
	fmt.Println(n.Name)
}`,
      [ok('Composite'), ok('Iterator'), no('Observer'), no('Decorator'), no('State')],
      'Nodes hold child nodes, and Walk lets callers loop over all of them.',
    ),
    spot1(
      'Which pattern is here, or is it misused?',
      `class Cart implements IteratorAggregate {
    private array $lines = [];
    function getIterator(): Generator {
        foreach ($this->lines as $l) {
            yield $l;
        }
    }
}
foreach ($cart as $line) { total($line); }`,
      [ok('Iterator'), no('Composite'), no('None of these'), no('Misused')],
      'It walks a flat list with an iterator; the lines never nest, so there is no composite.',
    ),
    judge(
      'An iterator over a composite must keep its own traversal state.',
      true,
      'Position, such as a stack of nodes, lives in the iterator so the tree stays unchanged.',
    ),
  ],
  'command-memento': [
    pick(
      'A paint app stores each stroke as an object. Before a stroke, it keeps a copy of the canvas. Which patterns?',
      [no('Observer'), ok('Command'), ok('Memento'), no('Flyweight')],
      'Strokes as stored actions are commands; the canvas copy for undo is a memento.',
    ),
    pick(
      'Which patterns together give you a redo and undo history?',
      [ok('Command'), no('Singleton'), no('Adapter'), ok('Memento'), no('Template Method')],
      'Commands record what to replay; mementos record the state to go back to.',
    ),
    spot(
      'Which patterns are here?',
      `class Move {
    private $was;
    function __construct(private $p) {}
    function run($to) {
        $this->was = $this->p->pos();
        $this->p->goto($to);
    }
    function undo() {
        $this->p->goto($this->was);
    }
}`,
      [ok('Command'), ok('Memento'), no('Strategy'), no('Mediator'), no('Prototype')],
      'Move is a command; it keeps the old position as a small memento to undo.',
    ),
    spot1(
      'Which pattern is here, or is it misused?',
      `type Undo struct{ stack []Snapshot }

func (u *Undo) Push(s Snapshot) {
	u.stack = append(u.stack, s)
}

func (u *Undo) Pop() Snapshot {
	s := u.stack[len(u.stack)-1]
	u.stack = u.stack[:len(u.stack)-1]
	return s
}`,
      [ok('Memento'), no('Command'), no('None of these'), no('Misused')],
      'It only stores snapshots; with no action objects, there is no command here.',
    ),
    judge(
      'A command can only be undone if it stores a memento.',
      false,
      'It can also undo by doing the inverse action, such as add and remove.',
    ),
  ],
  'observer-mediator': [
    pick(
      'A chat room object receives each message and forwards it to every member, who never talk directly. Which patterns?',
      [ok('Mediator'), no('Strategy'), ok('Observer'), no('Decorator')],
      'The room is the mediator; members subscribing to its messages act as observers.',
    ),
    pick(
      'Many widgets must react to each other. Which patterns keep them from holding references to one another?',
      [no('Prototype'), ok('Mediator'), no('Flyweight'), ok('Observer'), no('Builder')],
      'A mediator is the middle hub, and observers get notified instead of being called.',
    ),
    spot(
      'Which patterns are here?',
      `class Form {
    private array $on = [];
    function listen(string $f, callable $c)
    {
        $this->on[$f][] = $c;
    }
    function set(string $f, $v) {
        foreach ($this->on[$f] as $c) {
            $c($v);
        }
    }
}`,
      [ok('Observer'), ok('Mediator'), no('Command'), no('Proxy'), no('Composite')],
      'Fields subscribe through listen(); the Form sits between them and relays each change.',
    ),
    spot1(
      'Which pattern is here, or is it misused?',
      `type Stock struct {
	price float64
	subs  []func(float64)
}

func (s *Stock) Set(p float64) {
	s.price = p
	for _, f := range s.subs {
		f(p)
	}
}`,
      [ok('Observer'), no('Mediator'), no('None of these'), no('Misused')],
      'Subscribers are told when the price changes, but no hub sits between several parties.',
    ),
    judge(
      'With a mediator in place, observers must know about each other.',
      false,
      'The point is the opposite: each side only knows the hub.',
    ),
  ],
  'abstract-factory-singleton': [
    pick(
      'A test config makes fake database, mailer and clock together. One instance is shared by the whole app. Which patterns?',
      [ok('Abstract Factory'), no('Command'), ok('Singleton'), no('Observer')],
      'Related fakes from one maker form an abstract factory; sharing one instance is singleton.',
    ),
    pick(
      'Which patterns suit a single shared object that builds a matching family of parts?',
      [no('Builder'), ok('Singleton'), ok('Abstract Factory'), no('Proxy'), no('Iterator')],
      'The family is the abstract factory; the one-instance rule is the singleton.',
    ),
    spot(
      'Which patterns are here?',
      `type Kit interface {
	Disk() Disk
	Net() Net
}

var kit Kit
var once sync.Once

func GetKit() Kit {
	once.Do(func() { kit = newAwsKit() })
	return kit
}`,
      [ok('Abstract Factory'), ok('Singleton'), no('Bridge'), no('Facade'), no('Flyweight')],
      'Kit makes matching Disk and Net parts, and sync.Once keeps a single shared Kit.',
    ),
    spot1(
      'Which pattern is here, or is it misused?',
      `class Db {
    private static ?Db $i = null;
    private function __construct() {}
    static function get(): Db {
        return self::$i ??= new Db();
    }
    function query($sql) { /* ... */ }
}
$rows = Db::get()->query('select 1');`,
      [ok('Singleton'), no('Abstract Factory'), no('None of these'), no('Misused')],
      'One shared Db, but nothing builds a family of parts, so only singleton is here.',
    ),
    judge(
      'A Singleton is required for every Abstract Factory.',
      false,
      'One shared factory is common and handy, but you can create several without trouble.',
    ),
  ],
  'chain-of-responsibility-command': [
    pick(
      'A request passes checks that may reject it. The accepted request becomes a stored job run by a worker. Which patterns?',
      [no('Mediator'), ok('Command'), ok('Chain of Responsibility'), no('Flyweight')],
      'Checks that pass or stop form a chain; the stored job is a command.',
    ),
    pick(
      'Which patterns suit handlers that each may stop a request, and an action stored for later?',
      [ok('Chain of Responsibility'), no('Visitor'), no('Prototype'), ok('Command'), no('State')],
      'A chain lets each handler decide; a command packages the action to run later.',
    ),
    spot(
      'Which patterns are here?',
      `type Handler func(Req) error

func chain(hs ...Handler) Handler {
	return func(r Req) error {
		for _, h := range hs {
			if err := h(r); err != nil {
				return err
			}
		}
		return r.Job.Run()
	}
}`,
      [ok('Chain of Responsibility'), ok('Command'), no('Decorator'), no('Memento'), no('Proxy')],
      'Handlers run in order and any can stop; r.Job is a command run at the end.',
    ),
    spot1(
      'Which pattern is here, or is it misused?',
      `class Throttle {
    function handle($r, Closure $next) {
        if (tooMany($r->ip())) {
            return response('Slow', 429);
        }
        return $next($r);
    }
}`,
      [ok('Chain of Responsibility'), no('Command'), no('None of these'), no('Misused')],
      'It stops or passes on a request, a chain link; no job object is built, so no command.',
    ),
    judge(
      'In a chain, every handler must process the request.',
      false,
      'A handler may process it, pass it on, or stop the chain.',
    ),
  ],
  'template-method-strategy': [
    pick(
      'A base class fixes the order: read, check, save. Only the check rule is passed in as an object. Which patterns?',
      [no('Decorator'), ok('Template Method'), no('Singleton'), ok('Strategy')],
      'The fixed order is a template method; the injected check rule is a strategy.',
    ),
    pick(
      'Which patterns let you vary part of an algorithm while keeping its overall steps?',
      [ok('Strategy'), no('Facade'), no('Composite'), ok('Template Method'), no('Flyweight')],
      'Template Method varies steps by subclassing; Strategy varies them by passing an object.',
    ),
    spot(
      'Which patterns are here?',
      `abstract class Import {
    function run($f) {
        $rows = $this->parse($f);
        foreach ($rows as $r) {
            $this->rule->check($r);
        }
        $this->store($rows);
    }
    abstract function parse($f);
    abstract function store($rows);
}`,
      [ok('Template Method'), ok('Strategy'), no('Bridge'), no('Memento'), no('Proxy')],
      'run() fixes the steps with abstract gaps; $rule is a strategy handed in from outside.',
    ),
    spot1(
      'Which pattern is here, or is it misused?',
      `type Sorter struct {
	Less func(a, b int) bool
}

func (s Sorter) Sort(xs []int) {
	sort.Slice(xs, func(i, j int) bool {
		return s.Less(xs[i], xs[j])
	})
}

asc := Sorter{Less: lessInt}`,
      [ok('Strategy'), no('Template Method'), no('None of these'), no('Misused')],
      'The ordering is a swappable function; no base class fixes steps, so no template method.',
    ),
    judge(
      'Template Method relies on inheritance; Strategy relies on composition.',
      true,
      'Subclasses fill in the gaps of a template; a strategy is an object that is given to you.',
    ),
  ],
}
